import Alpine from "alpinejs";

// Sidenotes Store
Alpine.store("sidenotes", {
  active: null,
  highlighted: null,
  isMobile: false,

  init() {
    this.checkMobile();
    window.addEventListener("resize", () => this.checkMobile());
  },

  checkMobile() {
    this.isMobile = window.innerWidth < 1024;
  },

  setActive(id) {
    this.active = id;
  },

  clearActive() {
    this.active = null;
  },

  setHighlighted(id) {
    this.highlighted = id;
  },

  clearHighlighted() {
    this.highlighted = null;
  },
});

// Sidenotes Component
export function sidenotesComponent() {
  return {
    visibleSidenotes: new Set(),
    repositionTimeout: null,

    init() {
      // Initialize the store
      this.$store.sidenotes.init();

      // Set up intersection observer for sidenote visibility
      this.setupIntersectionObserver();

      // Set up resize handler
      this.setupResizeHandler();

      // Set up mobile click handlers
      if (this.$store.sidenotes.isMobile) {
        this.setupMobileHandlers();
      }

      // Listen for hover events
      this.setupHoverListeners();
    },

    setupIntersectionObserver() {
      if (!window.IntersectionObserver) return;

      const options = {
        rootMargin: "-20% 0px -20% 0px",
        threshold: 0,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const sidenote = entry.target;
          if (entry.isIntersecting) {
            sidenote.classList.add("sidenote-visible");
            this.visibleSidenotes.add(sidenote.id);
          } else {
            sidenote.classList.remove("sidenote-visible");
            this.visibleSidenotes.delete(sidenote.id);
          }
        });
      }, options);

      // Observe all sidenotes
      document.querySelectorAll(".sidenote").forEach((sidenote) => {
        observer.observe(sidenote);
      });
    },

    setupResizeHandler() {
      window.addEventListener("resize", () => {
        clearTimeout(this.repositionTimeout);
        this.repositionTimeout = setTimeout(() => {
          this.adjustLayout();
        }, 250);
      });
    },

    adjustLayout() {
      const isMobile = window.innerWidth < 1024;

      document.querySelectorAll(".paragraph-grid").forEach((grid) => {
        if (isMobile) {
          grid.classList.add("mobile-layout");
        } else {
          grid.classList.remove("mobile-layout");
        }
      });
    },

    setupMobileHandlers() {
      // Add click handlers for mobile sidenote markers
      document.querySelectorAll(".sidenote-marker").forEach((marker) => {
        marker.addEventListener("click", (e) => {
          if (this.$store.sidenotes.isMobile) {
            e.preventDefault();
            const noteId = marker.dataset.sidenoteId;
            this.toggleMobileSidenote(noteId);
          }
        });
      });
    },

    setupHoverListeners() {
      // Listen for custom hover events
      window.addEventListener("marker-hover", (e) => {
        if (e.detail.hover) {
          this.$store.sidenotes.setHighlighted(e.detail.id);
        } else {
          this.$store.sidenotes.clearHighlighted();
        }
      });
    },

    toggleMobileSidenote(noteId) {
      const sidenote = document.getElementById(noteId);
      if (!sidenote) return;

      // Toggle the sidenote
      if (this.$store.sidenotes.active === noteId) {
        sidenote.classList.remove("mobile-expanded");
        this.$store.sidenotes.clearActive();
      } else {
        // Close previously active sidenote
        if (this.$store.sidenotes.active) {
          const prevSidenote = document.getElementById(
            this.$store.sidenotes.active,
          );
          if (prevSidenote) {
            prevSidenote.classList.remove("mobile-expanded");
          }
        }

        // Open new sidenote
        sidenote.classList.add("mobile-expanded");
        this.$store.sidenotes.setActive(noteId);

        // Scroll to show the sidenote
        setTimeout(() => {
          sidenote.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }, 100);
      }
    },
  };
}

// Export for use in main app.js
export default function initSidenotes(Alpine) {
  Alpine.data("sidenotesComponent", sidenotesComponent);
}
